import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import { motion } from 'motion/react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { useMotionProps } from '@/lib/cva-motion';

const checkboxVariants = cva(
  'peer shrink-0 rounded-sm border ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        // Subtle checkbox: checked state uses accent border + check color, without filled background.
        default:
          'data-[state=checked]:border-primary data-[state=checked]:bg-transparent data-[state=checked]:text-primary',
        success:
          'data-[state=checked]:border-success data-[state=checked]:bg-transparent data-[state=checked]:text-success',
        warning:
          'data-[state=checked]:border-warning data-[state=checked]:bg-transparent data-[state=checked]:text-warning',
        emerald:
          'data-[state=checked]:border-emerald data-[state=checked]:bg-transparent data-[state=checked]:text-emerald',
        purple:
          'data-[state=checked]:border-purple data-[state=checked]:bg-transparent data-[state=checked]:text-purple',
        gold: 'data-[state=checked]:border-gold data-[state=checked]:bg-transparent data-[state=checked]:text-gold',
        pink: 'data-[state=checked]:border-pink data-[state=checked]:bg-transparent data-[state=checked]:text-pink',
      },
      size: {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface CheckboxProps
  extends
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, variant, size, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants({ variant, size }), className)}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
        <Check className={size === 'sm' ? 'h-2.5 w-2.5' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

/**
 * Motion-enhanced Checkbox component with automatic animations.
 *
 * Provides the same API as Checkbox but with built-in motion animations:
 * - Scale animation on check/uncheck
 * - Subtle scale on hover
 * - Smooth focus transitions
 *
 * @example
 * ```tsx
 * <MotionCheckbox variant="emerald" size="md" />
 * ```
 */
const MotionCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, size, ...props }, ref) => {
  const motionProps = useMotionProps({
    hover: 'scale',
    tap: 'scale',
    focus: 'glow',
    initial: 'none',
  });

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants({ variant, size }), className)}
      {...props}
      asChild
    >
      <motion.button
        {...(motionProps as any)}
        className={cn(checkboxVariants({ variant, size }), className)}
      >
        <CheckboxPrimitive.Indicator
          className={cn('flex items-center justify-center text-current')}
        >
          <Check
            className={size === 'sm' ? 'h-2.5 w-2.5' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'}
          />
        </CheckboxPrimitive.Indicator>
      </motion.button>
    </CheckboxPrimitive.Root>
  );
});
MotionCheckbox.displayName = 'MotionCheckbox';

export { Checkbox, checkboxVariants, MotionCheckbox };
